import type { Category, Media, Work } from "./types";
import { url } from "./api";

export function $(selector: string, parent: ParentNode = document): HTMLElement | null {
  return parent.querySelector(selector);
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function formatDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat("es-AR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function categoryName(id: string, cats: Category[]): string {
  return cats.find((c) => c.id === id || c.slug === id)?.name ?? id;
}

export function youtubeId(src: string): string | null {
  const match = src.match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{11})/);
  return match ? match[1] : null;
}

export function mediaSrc(src: string): string {
  if (/^https?:\/\//i.test(src)) return src;
  return url(src);
}

export function renderMedia(item: Media, className = ""): string {
  const src = mediaSrc(item.src);
  const alt = escapeHtml(item.alt || "");
  if (item.type === "youtube") {
    const id = youtubeId(item.src);
    if (!id) return "";
    return `<iframe class="${className}" src="https://www.youtube.com/embed/${id}" title="${alt}" allowfullscreen loading="lazy"></iframe>`;
  }
  if (item.type === "video") {
    return `<video class="${className}" src="${escapeHtml(src)}" muted loop playsinline autoplay preload="metadata"></video>`;
  }
  return `<img class="${className}" src="${escapeHtml(src)}" alt="${alt}" loading="lazy" />`;
}

export function coverOf(work: Work): Media | null {
  return work.media[0] ?? null;
}

export function pieceCard(work: Work, cats: Category[]): string {
  const cover = coverOf(work);
  const media = cover ? renderMedia(cover) : "";
  return `<article>
    <a class="piece" href="${url(`trabajo.html?id=${encodeURIComponent(work.id)}`)}">
      <div class="piece-media">${media}</div>
      <div class="piece-meta">
        <span>${escapeHtml(work.title)}</span>
        <small>${escapeHtml(categoryName(work.category, cats))}</small>
      </div>
    </a>
  </article>`;
}

export function featuredWorks(works: Work[]): Work[] {
  return works
    .filter((w) => w.featured)
    .sort((a, b) => a.featuredOrder - b.featuredOrder || b.createdAt.localeCompare(a.createdAt));
}

export function bindSiteText(site: { [key: string]: string }): void {
  document.querySelectorAll<HTMLElement>("[data-bind]").forEach((el) => {
    const key = el.dataset.bind;
    if (key && site[key]) el.textContent = site[key];
  });
}

export function setYear(): void {
  document.querySelectorAll("[data-year]").forEach((el) => {
    el.textContent = String(new Date().getFullYear());
  });
}
